import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Bitcoin, Minus, Plus, ShieldCheck, Mail, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtFiat } from "@/lib/api";

export default function PayLink() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [promo, setPromo] = useState("");
  const [quote, setQuote] = useState(null);

  useEffect(() => {
    api.get(`/v1/pay/${slug}`).then((r) => setP(r.data)).catch((e) => setErr(errMsg(e)));
  }, [slug]);
  useEffect(() => {
    if (!quote?.promo) return;
    api.post(`/v1/pay/${slug}/quote`, { quantity: qty, promoCode: quote.promo.code }).then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [qty]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPromo = async () => {
    try { const { data } = await api.post(`/v1/pay/${slug}/quote`, { quantity: qty, promoCode: promo }); setQuote(data); toast.success(`Code ${data.promo.code} appliqué`); }
    catch (e) { setQuote(null); toast.error(errMsg(e)); }
  };

  const pay = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/v1/pay/${slug}`, { quantity: qty, promoCode: quote?.promo ? quote.promo.code : undefined }); nav(`/checkout/${data.orderId}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen grid place-items-center text-zinc-400 text-sm" data-testid="paylink-error">{err}</div>;
  if (!p) return <div className="min-h-screen grid place-items-center text-zinc-500 text-sm">Chargement…</div>;
  const subtotal = p.price * qty;
  const total = quote?.promo ? quote.total : subtotal;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-8">
      <div className="max-w-md w-full mx-auto p-6 sm:p-8 bg-zinc-900 border border-zinc-800 rounded-2xl shadow-2xl fade-up" data-testid="paylink-card">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-md bg-amber-500 grid place-items-center text-zinc-950"><Bitcoin size={18} strokeWidth={2.5} /></span>
            <div className="text-sm font-semibold" data-testid="paylink-merchant-name">{p.merchantName}</div>
          </div>
          {p.mode === "test" && <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">TESTNET</span>}
        </div>
        {p.imageUrl && <img src={p.imageUrl} alt={p.name} className="w-full h-48 object-cover rounded-xl mb-5 border border-zinc-800" data-testid="paylink-product-image" />}
        <div className="eyebrow mb-1">Produit</div>
        <h1 className="text-2xl font-bold tracking-tight" data-testid="paylink-product-name">{p.name}</h1>
        {p.description && <p className="text-sm text-zinc-400 mt-2 leading-relaxed" data-testid="paylink-description">{p.description}</p>}
        <div className="mt-5 text-3xl font-heading font-bold tracking-tight" data-testid="paylink-price">{fmtFiat(p.price, p.currency)}</div>
        <div className="text-xs text-zinc-500 mt-1">{p.codesPerOrder} code{p.codesPerOrder > 1 ? "s" : ""} livré{p.codesPerOrder > 1 ? "s" : ""} par unité, après confirmation du paiement</div>

        {p.inStock ? (<>
          <div className="mt-6 flex items-center justify-between border-t border-zinc-800 pt-5">
            <span className="text-sm text-zinc-300">Quantité</span>
            <div className="flex items-center gap-3">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="h-8 w-8 rounded-md border border-zinc-700 grid place-items-center hover:bg-zinc-800" data-testid="paylink-qty-minus"><Minus size={14} /></button>
              <span className="font-mono text-lg w-8 text-center" data-testid="paylink-qty">{qty}</span>
              <button onClick={() => setQty(Math.min(p.maxQuantity, qty + 1))} className="h-8 w-8 rounded-md border border-zinc-700 grid place-items-center hover:bg-zinc-800" data-testid="paylink-qty-plus"><Plus size={14} /></button>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <input className="flex-1 h-9 rounded-md border border-zinc-700 bg-zinc-950 px-3 text-sm font-mono uppercase placeholder:normal-case placeholder:font-sans placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-amber-500/50" placeholder="Code promo" value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} data-testid="paylink-promo-input" />
            <button onClick={applyPromo} disabled={!promo} className="h-9 px-3 rounded-md border border-zinc-700 text-sm hover:bg-zinc-800 disabled:opacity-40" data-testid="paylink-apply-promo-button">Appliquer</button>
          </div>
          {quote?.promo && <div className="mt-3 flex items-center justify-between text-sm" data-testid="paylink-discount"><span className="text-emerald-400 font-mono text-xs">{quote.promo.code} · {quote.promo.kind === "percent" ? `-${quote.promo.value} %` : `-${fmtFiat(quote.promo.value, p.currency)}`}</span><span className="text-emerald-400">−{fmtFiat(quote.discount, p.currency)}</span></div>}
          <div className="mt-3 flex items-center justify-between text-sm"><span className="text-zinc-400">Total</span><span className="font-semibold" data-testid="paylink-total">{quote?.promo && <span className="text-zinc-500 line-through mr-2 font-normal">{fmtFiat(subtotal, p.currency)}</span>}{fmtFiat(total, p.currency)}</span></div>
          <button onClick={pay} disabled={busy} className="mt-5 w-full h-11 rounded-md bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold transition-colors disabled:opacity-50 active:scale-[.98] flex items-center justify-center gap-2" data-testid="paylink-pay-button">
            <Bitcoin size={16} /> {busy ? "Création…" : "Payer en Bitcoin"}
          </button>
        </>) : (
          <div className="mt-6 p-4 rounded-md border border-red-500/30 bg-red-500/5 text-sm text-red-300" data-testid="paylink-out-of-stock">Rupture de stock : ce produit n'est plus disponible pour le moment.</div>
        )}

        <div className="mt-6 grid grid-cols-3 gap-2 text-[10px] text-zinc-500 border-t border-zinc-800 pt-4">
          <div className="flex items-center gap-1"><Mail size={11} /> E-mail vérifié</div>
          <div className="flex items-center gap-1"><ShieldCheck size={11} /> Non-custodial</div>
          <div className="flex items-center gap-1"><PackageCheck size={11} /> Codes par e-mail</div>
        </div>
      </div>
    </div>
  );
}
